import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoMemo from '@/components/Formulario/CampoMemo';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoSenha from '@/components/Formulario/CampoSenha';
import CampoTexto from '@/components/Formulario/CampoTexto';
import { Accordion } from '@/components/layout/Accordion';
import Grid, { GridItem } from '@/components/layout/Grid';
import HBox from '@/components/layout/HBox';
import { mdiCancel, mdiCheck } from '@mdi/js';
import Icon from '@mdi/react';
import { ApplianceDTO } from '@webip/shared/src/dto/appliance';
import type { AgenteDTO } from '@webip/shared/src/dto/agente';
import { useQuery } from 'react-query';
import axios from 'axios';

const agentTypeOptions = [
    { id: 'mikrotik', label: 'MikroTik' },
    { id: 'opnsense', label: 'OPNSense' },
];

const protocolOptions = [
    { id: 'http', label: 'HTTP' },
    { id: 'https', label: 'HTTPS' },
];

type FormApplianceProps = {
    value: ApplianceDTO;
    onChange: (value: ApplianceDTO) => void;
    fieldErrors: Record<string, string>;
};

export const FormAppliance = ({
    value,
    onChange,
    fieldErrors,
}: FormApplianceProps) => {
    const agentType = value?.agent_type ?? 'mikrotik';
    const isOpnsense = agentType === 'opnsense';

    // Agentes existentes pro seletor. O cadastro/edição do agente em si mora
    // na tela de Agentes: com N appliances sob o mesmo agent_id, editar por
    // aqui obrigava a eleger uma appliance arbitrária como dona do agente.
    const queryAgentes = useQuery({
        queryKey: ['agentes'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: AgenteDTO[] }>('/api/agente', { withCredentials: true });
            return res.data.result;
        },
        staleTime: 30_000,
    });

    const agenteSelecionado = (queryAgentes.data ?? []).find(a => a.agent_id === value?.agent_id);
    const semAgentes = !queryAgentes.isLoading && !(queryAgentes.data ?? []).length;

    const agenteOptions = [
        // Placeholder vazio, nunca uma opção de "gerar um agente". O agente é
        // instalado antes, na tela de Agentes, e a appliance só o referencia:
        // criar um id aqui produzia uma appliance apontando pra um agente
        // inexistente, com cara de configurada. Numa appliance já salva com
        // agente o placeholder sai da lista, porque desvincular não é
        // suportado — o PUT preserva o agent_id quando o campo vem vazio.
        ...(!value?.id || !value?.agent_id
            ? [{ id: '', label: value?.id ? '— Nenhum —' : '— Selecione um agente —' }]
            : []),
        ...(queryAgentes.data ?? []).map(a => ({
            id: a.agent_id,
            label: `${a.agent_id}${a.compartilhado ? ' · compartilhado' : ''}${a.appliances.length ? ` · ${a.appliances[0].nome}${a.appliances.length > 1 ? ` (+${a.appliances.length - 1})` : ''}` : ' · sem appliance'}${a.online ? ' · online' : ''}`,
        })),
        // Agente já gravado nesta appliance que sumiu da lista (fora do escopo
        // do usuário, por exemplo): sem isto o seletor apagaria o vínculo só
        // por abrir o formulário.
        ...(value?.agent_id && !agenteSelecionado ? [{ id: value.agent_id, label: `${value.agent_id} (não listado)` }] : []),
    ];


    return (
        <Grid>
            <div style={{ position: 'absolute', height: 0 }}>
                <input
                    type="text"
                    name="username"
                    style={{
                        height: 0,
                        width: 0,
                        border: 'none',
                        outline: 'none',
                        margin: 0,
                        padding: 0,
                    }}
                />
                <input
                    type="password"
                    name="password"
                    style={{
                        height: 0,
                        width: 0,
                        border: 'none',
                        outline: 'none',
                        margin: 0,
                        padding: 0,
                    }}
                />
            </div>
            <GridItem sm={12}>
                <CampoTexto
                    value={value?.nome ?? ''}
                    onChange={v => onChange({ ...value, nome: v })}
                    label="Nome"
                    error={fieldErrors?.nome}
                />
            </GridItem>
            <GridItem sm={12}>
                <CampoMemo
                    value={value?.descricao ?? ''}
                    onChange={v => onChange({ ...value, descricao: v })}
                    label="Descrição"
                    error={fieldErrors?.descricao}
                />
            </GridItem>
            <GridItem sm={12}>
                <CampoSeletor
                    value={value?.agent_id ?? ''}
                    onChange={v => onChange({ ...value, agent_id: v || undefined })}
                    label="Agente"
                    options={agenteOptions}
                    error={fieldErrors?.agent_id}
                    hint={value?.id
                        ? 'Quem opera esta appliance. IP, tokens e RADIUS secret do agente ficam em Agentes — são compartilhados por todas as appliances que ele atende.'
                        : semAgentes
                            ? 'Obrigatório. Nenhum agente disponível ainda: instale um em Agentes e volte aqui depois que ele conectar.'
                            : 'Obrigatório: o agente que vai operar esta appliance. Ele precisa estar instalado e conectado antes — a instalação é feita em Agentes.'}
                />
            </GridItem>
            <GridItem sm={12}>
                <Accordion title="Conexão" startOpen={true}>
                    <Grid>
                        <GridItem sm={12} md={4}>
                            <CampoSeletor
                                noSearch
                                value={agentType}
                                onChange={v => onChange({
                                    ...value,
                                    agent_type: (v as ApplianceDTO['agent_type']) ?? 'mikrotik',
                                    snmp_enabled: value?.snmp_enabled ?? false,
                                })}
                                label="Tipo de appliance"
                                options={agentTypeOptions}
                                error={fieldErrors?.agent_type}
                                hint="Define qual integração o backend e o agente devem usar para discovery e sync."
                            />
                        </GridItem>
                        <GridItem sm={4} md={2}>
                            <CampoSeletor
                                noSearch
                                value={value?.ssl ? 'https' : 'http'}
                                onChange={v => {
                                    const isHttps = v === 'https';
                                    onChange({ ...value, ssl: isHttps, porta: isHttps ? (value?.porta ?? 443) : (value?.porta ?? 80) });
                                }}
                                label="Protocolo"
                                options={protocolOptions}
                            />
                        </GridItem>
                        <GridItem sm={8} md={4}>
                            <CampoTexto
                                label={isOpnsense ? 'Host/IP' : 'IP'}
                                value={value?.enderecos?.[0] ?? ''}
                                onChange={v => {
                                    onChange({ ...value, enderecos: [v] });
                                }}
                                error={fieldErrors?.enderecos}
                                placeholder="192.168.1.1"
                            />
                        </GridItem>
                        <GridItem sm={12} md={2}>
                            <CampoNumero
                                label="Porta"
                                value={String(value?.porta ?? (value?.ssl ? 443 : 80))}
                                onChange={v => onChange({ ...value, porta: v ? Number(v) : null })}
                                somentePositivos
                            />
                        </GridItem>
                        <GridItem sm={12} md={4}>
                            <CampoTexto
                                label={isOpnsense ? 'API Key' : 'Usuário'}
                                value={value?.usuario ?? ''}
                                onChange={v => {
                                    onChange({ ...value, usuario: v });
                                }}
                                error={fieldErrors?.usuario}
                                placeholder={isOpnsense ? 'key da API' : 'admin'}
                            />
                        </GridItem>
                        <GridItem sm={12} md={4}>
                            <CampoSenha
                                label={isOpnsense ? 'API Secret' : 'Senha'}
                                value={value?.senha ?? ''}
                                onChange={v => {
                                    onChange({ ...value, senha: v });
                                }}
                                error={fieldErrors?.senha}
                                placeholder={value?.id ? '••••••••' : ''}
                            />
                        </GridItem>
                    </Grid>
                </Accordion>
            </GridItem>
            {isOpnsense && (
                <GridItem sm={12}>
                    <Accordion title="Acesso SSH (OPNsense)" startOpen={false}>
                        <Grid>
                            <GridItem sm={12} md={4}>
                                <CampoTexto
                                    label="Usuário SSH"
                                    value={value?.opnsense_ssh_usuario ?? ''}
                                    onChange={v => onChange({ ...value, opnsense_ssh_usuario: v })}
                                    error={fieldErrors?.opnsense_ssh_usuario}
                                    placeholder="root"
                                />
                            </GridItem>
                            <GridItem sm={12} md={4}>
                                <CampoNumero
                                    label="Porta SSH"
                                    value={String(value?.opnsense_ssh_porta ?? 22)}
                                    onChange={v => onChange({ ...value, opnsense_ssh_porta: v ? Number(v) : undefined })}
                                    error={fieldErrors?.opnsense_ssh_porta}
                                    somentePositivos
                                />
                            </GridItem>
                            <GridItem sm={12} md={4}>
                                <CampoSenha
                                    label="Senha SSH"
                                    value={value?.opnsense_ssh_senha ?? ''}
                                    onChange={v => onChange({ ...value, opnsense_ssh_senha: v })}
                                    error={fieldErrors?.opnsense_ssh_senha}
                                    placeholder={value?.id ? '••••••••' : ''}
                                />
                            </GridItem>
                        </Grid>
                        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--color-muted)' }}>
                            A API REST do OPNsense não cobre configuração de IP/gateway de interface WAN — o agente usa SSH para editar o config.xml diretamente. Sem essas credenciais, o sync de WAN é ignorado.
                        </p>
                    </Accordion>
                </GridItem>
            )}
            {!isOpnsense && (
                <GridItem sm={12}>
                    <Accordion title={
                        <HBox>
                            <span>Associação IP ↔ MAC</span>
                            <Icon
                                path={
                                    (value?.associar_ip_mac ?? true) ? mdiCheck : mdiCancel
                                }
                                size={0.75}
                            />
                        </HBox>
                    } startOpen={false}>
                        <CampoCheckbox
                            label="Dispositivos LAN"
                            text="Bloquear tráfego do IP de um dispositivo vindo de MAC diferente do cadastrado"
                            value={value?.associar_ip_mac ?? true}
                            onChange={checked => onChange({ ...value, associar_ip_mac: checked })}
                            hint="Desligado, o próximo sync remove as regras de bloqueio do MikroTik. As reservas DHCP (IP fixo por MAC) continuam."
                        />
                    </Accordion>
                </GridItem>
            )}
            <GridItem sm={12}>
                <Accordion title={
                    <HBox>
                        <span>SNMP</span>
                        <Icon
                            path={
                                value?.snmp_enabled ? mdiCheck : mdiCancel
                            }
                            size={0.75}
                        />
                    </HBox>
                } startOpen={false}>
                    <Grid>
                        <GridItem sm={12}>
                            <CampoCheckbox
                                label="Telemetria"
                                text="Habilitar coleta via SNMP"
                                value={value?.snmp_enabled ?? false}
                                onChange={checked => onChange({
                                    ...value,
                                    snmp_enabled: checked,
                                    snmp_version: checked ? (value?.snmp_version ?? '2c') : value?.snmp_version,
                                    snmp_port: checked ? (value?.snmp_port ?? 161) : value?.snmp_port,
                                })}
                                hint={isOpnsense ? "Configure o serviço SNMP na appliance OPNsense; o agente consultará periodicamente via SNMPv2c." : "O agente prepara o MikroTik no sync e depois consulta periodicamente a appliance via SNMP."}
                            />
                        </GridItem>
                        <GridItem sm={12} md={4}>
                            <CampoTexto
                                label="Versão"
                                value={value?.snmp_version ?? '2c'}
                                onChange={() => undefined}
                                disabled
                                hint="Nesta fase, apenas SNMP v2c é suportado."
                            />
                        </GridItem>
                        <GridItem sm={12} md={4}>
                            <CampoNumero
                                label="Porta"
                                value={String(value?.snmp_port ?? 161)}
                                onChange={v => onChange({ ...value, snmp_port: Number(v || 161) })}
                                error={fieldErrors?.snmp_port}
                                somentePositivos
                                disabled={!value?.snmp_enabled}
                            />
                        </GridItem>
                        <GridItem sm={12} md={4}>
                            <CampoTexto
                                label="Community"
                                value={value?.snmp_community ?? ''}
                                onChange={v => onChange({ ...value, snmp_community: v })}
                                error={fieldErrors?.snmp_community}
                                disabled={!value?.snmp_enabled}
                                placeholder="deixe em branco para gerar automaticamente"
                                hint="Fica mascarada nas respostas da API. Se vazia, o backend gera uma community gerenciada."
                            />
                        </GridItem>
                    </Grid>
                    {!isOpnsense && (
                        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--color-muted)' }}>
                            O campo IP do Agente é usado para restringir quem pode consultar o SNMP no MikroTik.
                        </p>
                    )}
                </Accordion>
            </GridItem>
        </Grid>
    );
};
